/**
 * Shared authentication utilities
 */

import crossTabSync from './crossTabSync';

const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

// ============================================================================
// P1 PERFORMANCE: Cached user profile to reduce /api/auth/me calls
// Module-level cache variables (defined here for use in clearAuthTokens)
// ============================================================================
let cachedUserProfile = null;
let userProfilePromise = null;
const USER_CACHE_KEY = 'cached_user_profile';
const USER_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get the current access token from localStorage
 * @returns {string|null} The access token or null if not found
 */
export const getAuthToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get the current refresh token from localStorage
 * @returns {string|null} The refresh token or null if not found
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Set the access token in localStorage
 * @param {string} token - The access token to store
 */
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  }
};

/**
 * Set the refresh token in localStorage
 * @param {string} token - The refresh token to store
 */
export const setRefreshToken = (token) => {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }
};

/**
 * Clear all authentication tokens from localStorage
 * @param {Object} options - Options for clearing
 * @param {boolean} options.broadcast - Whether to broadcast logout to other tabs (default: true)
 */
export const clearAuthTokens = ({ broadcast = true } = {}) => {
  // Stop proactive refresh timer on logout
  stopTokenRefreshTimer();
  
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('activeAccountId');
  
  // Also clear user profile cache on logout
  cachedUserProfile = null;
  try {
    sessionStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // Ignore errors
  }

  // Subscription/trial state is per-user and is cached both in localStorage and
  // in an in-memory memo. Logout is client-side (no reload), so without this a
  // different user signing in within the TTL would briefly see the previous
  // user's trial banner and plan state.
  try {
    localStorage.removeItem('cachedSubscriptionStatus');
    sessionStorage.removeItem('trialBannerDismissedAt');
  } catch {
    // Ignore errors
  }
  window.dispatchEvent(new Event('auth:cleared'));
  
  // Broadcast logout to other tabs
  if (broadcast) {
    crossTabSync.broadcastLogout();
  }
  
  // Note: React Query cache is cleared by the component that calls this
  // (App.js handles queryClient.clear() on logout)
};

/**
 * Check if user has a stored token (not necessarily valid)
 * @returns {boolean} True if a token exists
 */
export const hasAuthToken = () => {
  return !!getAuthToken();
};

/**
 * Get the active account ID for multi-account support
 * @returns {string|null} The active account ID or null if not set
 */
export const getActiveAccountId = () => {
  return localStorage.getItem('activeAccountId');
};

/**
 * Get authorization header object for fetch requests
 * Includes X-Account-Id header for multi-account/team support
 * @returns {Object} Headers object with Authorization and optional X-Account-Id
 */
export const getAuthHeaders = () => {
  const token = getAuthToken();
  const accountId = getActiveAccountId();
  
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (accountId) {
    headers['X-Account-Id'] = accountId;
  }
  return headers;
};

/**
 * Get full headers for JSON API requests
 * Includes Authorization, Content-Type, and X-Account-Id for team support
 * @returns {Object} Headers object with Authorization, Content-Type, and optional X-Account-Id
 */
export const getApiHeaders = () => {
  return {
    ...getAuthHeaders(),
    'Content-Type': 'application/json'
  };
};

/**
 * Decode JWT token payload (does not verify signature)
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

/**
 * Check if token is expired based on exp claim
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired or invalid
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // exp is in seconds, Date.now() is in milliseconds
  // Add 30 second buffer to refresh before actual expiry
  return decoded.exp * 1000 < Date.now() + 30000;
};

/**
 * Get user ID from stored token
 * @returns {string|null} User ID or null if not available
 */
export const getUserIdFromToken = () => {
  const token = getAuthToken();
  const decoded = decodeToken(token);
  return decoded?.sub || decoded?.userId || decoded?._id || null;
};

// Track if we're currently refreshing to prevent multiple simultaneous refreshes
let isRefreshing = false;
let refreshPromise = null;

// ============================================================================
// BUG-010 FIX: Proactive Token Refresh Timer
// The access token expires in 1 hour, but ~260 API calls across 50+ files use
// getAuthHeaders()/getApiHeaders() with raw fetch() — NO token refresh.
// This timer proactively refreshes the token BEFORE it expires so those calls
// always have a valid token. Zero changes needed to existing call sites.
// ============================================================================
let refreshTimerId = null;
const REFRESH_CHECK_INTERVAL = 60 * 1000; // Check every 60 seconds
const REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000; // Refresh 5 minutes before expiry

/**
 * Check if the access token needs proactive refresh
 * Triggers if: (a) token is within 5 min of expiry, OR (b) token is already expired
 * Case (b) handles laptop sleep / tab backgrounding where the timer was paused
 * @returns {boolean} True if token should be refreshed
 */
const shouldProactivelyRefresh = () => {
  const token = getAuthToken();
  if (!token) return false;
  
  // If refresh token is also expired, no point trying
  const refreshToken = getRefreshToken();
  if (!refreshToken || isTokenExpired(refreshToken)) return false;
  
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return false;
  
  const expiresAt = decoded.exp * 1000; // Convert to ms
  const timeUntilExpiry = expiresAt - Date.now();
  
  // Refresh if already expired OR within 5 minutes of expiry
  // Already-expired case handles laptop sleep / tab backgrounding
  return timeUntilExpiry < REFRESH_BEFORE_EXPIRY;
};

/**
 * Perform the proactive refresh check and execute refresh if needed
 */
const doProactiveRefreshCheck = async () => {
  try {
    if (shouldProactivelyRefresh()) {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        console.warn('[TokenRefresh] Proactive refresh failed — session may expire soon');
        stopTokenRefreshTimer();
      }
    }
  } catch (error) {
    console.error('[TokenRefresh] Error during proactive refresh:', error);
  }
};

/**
 * Handle tab becoming visible again — immediately check token freshness.
 * Browsers throttle setInterval in background tabs, so the 60s timer
 * might not fire for minutes. This ensures an instant check on return.
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    doProactiveRefreshCheck();
  }
};

/**
 * Start the proactive token refresh timer.
 * Call this after login or on app mount when user is authenticated.
 * Checks every 60 seconds if the token needs refreshing.
 * Also listens for tab visibility changes for instant checks on return.
 */
export const startTokenRefreshTimer = () => {
  // Clear any existing timer
  stopTokenRefreshTimer();
  
  refreshTimerId = setInterval(doProactiveRefreshCheck, REFRESH_CHECK_INTERVAL);
  
  // Also check immediately when tab becomes visible (handles laptop sleep / background tabs)
  document.addEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * Stop the proactive token refresh timer.
 * Call this on logout.
 */
export const stopTokenRefreshTimer = () => {
  if (refreshTimerId) {
    clearInterval(refreshTimerId);
    refreshTimerId = null;
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * Get current user profile with caching
 * P1 PERFORMANCE: Caches user profile per session to reduce API calls
 * 
 * @param {Object} options - Options for fetching
 * @param {boolean} options.forceRefresh - Skip cache and fetch fresh data
 * @returns {Promise<Object|null>} User profile or null if not authenticated
 */
export const getCachedUserProfile = async ({ forceRefresh = false } = {}) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3333';
  
  // If already fetching, wait for that to complete (deduplicate concurrent calls)
  if (userProfilePromise && !forceRefresh) {
    return userProfilePromise;
  }
  
  // Check cache first (unless force refresh)
  if (!forceRefresh && cachedUserProfile) {
    const cacheAge = Date.now() - (cachedUserProfile._cachedAt || 0);
    if (cacheAge < USER_CACHE_TTL) {
      return cachedUserProfile;
    }
  }
  
  // Also try sessionStorage for cross-tab consistency
  if (!forceRefresh) {
    try {
      const cached = sessionStorage.getItem(USER_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cacheAge = Date.now() - (parsed._cachedAt || 0);
        if (cacheAge < USER_CACHE_TTL) {
          cachedUserProfile = parsed;
          return parsed;
        }
      }
    } catch {
      // Ignore sessionStorage errors
    }
  }
  
  // Fetch fresh data
  const token = getAuthToken();
  if (!token) {
    return null;
  }
  
  userProfilePromise = fetch(`${BACKEND_URL}/api/auth/me`, {
    headers: getAuthHeaders()
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }
      const data = await response.json();
      const profile = data.user || data;
      profile._cachedAt = Date.now();
      
      // Update memory cache
      cachedUserProfile = profile;
      
      // Update sessionStorage
      try {
        sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(profile));
      } catch {
        // Ignore sessionStorage errors
      }
      
      return profile;
    })
    .catch((error) => {
      console.error('Failed to fetch user profile:', error);
      return null;
    })
    .finally(() => {
      userProfilePromise = null;
    });
  
  return userProfilePromise;
};

/**
 * Invalidate cached user profile
 * Call this when user data changes (e.g., after updating settings)
 */
export const invalidateUserProfileCache = () => {
  cachedUserProfile = null;
  try {
    sessionStorage.removeItem(USER_CACHE_KEY);
  } catch {
    // Ignore errors
  }
};

/**
 * Refresh the access token using the refresh token
 * @returns {Promise<string|null>} New access token or null if refresh failed
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken || isTokenExpired(refreshToken)) {
    clearAuthTokens();
    return null;
  }
  
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  isRefreshing = true;
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3333';
  
  refreshPromise = fetch(`${BACKEND_URL}/api/auth/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error('Token refresh failed');
      }
      const data = await response.json();
      setAuthToken(data.accessToken);
      return data.accessToken;
    })
    .catch((error) => {
      console.error('Failed to refresh token:', error);
      clearAuthTokens();
      return null;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });
  
  return refreshPromise;
};

/**
 * Authenticated fetch wrapper that handles token refresh
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export const authFetch = async (url, options = {}) => {
  let token = getAuthToken();
  
  // Check if token is expired and refresh if needed
  if (token && isTokenExpired(token)) {
    token = await refreshAccessToken();
    if (!token) {
      // Trigger logout event for App.js to handle
      window.dispatchEvent(new CustomEvent('auth:logout', { 
        detail: { reason: 'session_expired' } 
      }));
      throw new Error('Session expired. Please login again.');
    }
  }
  
  // Make the request with the token
  const headers = {
    ...options.headers,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
  
  const response = await fetch(url, { ...options, headers });
  
  // If we get a 401, try to refresh once and retry
  if (response.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      return fetch(url, { ...options, headers });
    } else {
      // Trigger logout event
      window.dispatchEvent(new CustomEvent('auth:logout', { 
        detail: { reason: 'session_expired' } 
      }));
      throw new Error('Session expired. Please login again.');
    }
  }
  
  return response;
};

export default {
  getAuthToken,
  getRefreshToken,
  setAuthToken,
  setRefreshToken,
  clearAuthTokens,
  hasAuthToken,
  getAuthHeaders,
  getApiHeaders,
  decodeToken,
  isTokenExpired,
  getUserIdFromToken,
  refreshAccessToken,
  authFetch,
  getCachedUserProfile,
  invalidateUserProfileCache,
  startTokenRefreshTimer,
  stopTokenRefreshTimer,
};
